import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ActionMenu } from '@core/components/ui';

/**
 * File Table Actions Component
 *
 * Renders 3-dot action menu for file/folder operations.
 * Used in the Actions column of the FileTable.
 *
 * @param {Object} props
 * @param {Object} props.item - File or folder object
 * @param {Function} props.onDownload - Download handler (files only)
 * @param {Function} props.onDelete - Delete handler
 * @param {Function} props.onRename - Rename handler (folders only)
 * @param {Function} props.onMove - Move handler (files only)
 * @param {boolean} [props.isDownloading=false] - Whether file is downloading
 *
 * @example
 * <FileTableActions
 *   item={row.original}
 *   onDownload={() => handleDownload(row.original)}
 *   onDelete={() => handleDelete(row.original)}
 *   onRename={() => handleRename(row.original)}
 *   onMove={() => handleMove(row.original)}
 *   isDownloading={downloadingFile === row.original.id}
 * />
 */
export function FileTableActions({ item, onDownload, onDelete, onRename, onMove, isDownloading = false }) {
  const { t } = useTranslation();

  // Build menu items based on item type
  const menuItems = useMemo(() => {
    const isFolder = item.type === 'folder';

    if (isFolder) {
      // Folder actions: Rename, Delete
      return [
        {
          label: t('files.explorer.rename', 'Renombrar'),
          icon: 'mdi:pencil',
          onClick: onRename,
        },
        {
          label: t('files.explorer.delete', 'Eliminar'),
          icon: 'mdi:delete',
          onClick: onDelete,
          variant: 'danger',
        },
      ];
    }

    // File actions: Download, Move, Delete
    return [
      {
        label: t('files.explorer.download', 'Descargar'),
        icon: 'mdi:download',
        onClick: onDownload,
        disabled: isDownloading,
      },
      {
        label: t('files.explorer.moveToFolder', 'Mover a carpeta'),
        icon: 'mdi:folder-move',
        onClick: onMove,
      },
      {
        label: t('files.explorer.delete', 'Eliminar'),
        icon: 'mdi:delete',
        onClick: onDelete,
        variant: 'danger',
      },
    ];
  }, [item, onDownload, onDelete, onRename, onMove, isDownloading, t]);

  return <ActionMenu items={menuItems} />;
}

FileTableActions.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['file', 'folder']),
    file_count: PropTypes.number,
  }).isRequired,
  onDownload: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onRename: PropTypes.func,
  onMove: PropTypes.func,
  isDownloading: PropTypes.bool,
};
